/* ==========================================================================
   Wattec — keep the chat button clear of the bottom of the screen

   Shopify Inbox's round "Chat" button sits at a fixed height chosen in the
   Inbox app (lowest / higher / highest). The bottom of the screen also holds
   the floating Add to Cart bar and the back-to-top button, so at some of
   those heights the chat button lands on top of one of them.

   On every screen size the button rests down in the bottom corner (the
   Inbox height was only ever chosen to clear the phone's bottom menu bar,
   which is no longer shown), and it is lifted only by as much as it
   needs above whichever of those it would otherwise cover — then drops back
   when they move away (the floating bar hides, for instance). Nothing else
   on the page is moved; the chat window itself is untouched.

   The chat is Shopify's own component, sealed in its own shadow root. A
   small style is added inside it that moves the button by a CSS variable
   (--wt-chat-lift) this script sets on the page; if Shopify ever changes
   the component and the button can't be found, this simply does nothing.
   ========================================================================== */
(function () {
  if (window.__wattecChatPosition) return;
  window.__wattecChatPosition = true;

  var GAP = 10;
  // The button rests in the corner, RESTING_GAP px above the bottom edge,
  // instead of at the Inbox app's height — on every screen size.
  var RESTING_GAP = 16;
  var root = document.documentElement;
  var lift = 0;

  function chatButton() {
    var host = document.querySelector('shopify-chat');
    var shadow = host && host.shadowRoot;
    if (!shadow) return null;
    if (!shadow.querySelector('style[data-wt-chat-lift]')) {
      var style = document.createElement('style');
      style.setAttribute('data-wt-chat-lift', '');
      style.textContent = '.activator-nudge{transform:translateY(calc(-1 * var(--wt-chat-lift, 0px)));transition:transform .3s ease,opacity .2s ease,visibility .2s ease;}' +
        ':host(.wt-drawer-hide) .activator-nudge{opacity:0;visibility:hidden;pointer-events:none;}' +
        '@media (prefers-reduced-motion: reduce){.activator-nudge{transition:none;}}';
      shadow.appendChild(style);
    }
    return shadow.querySelector('.activator-nudge');
  }

  function visible(el) {
    if (!el) return false;
    var cs = window.getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0 && el.getClientRects().length > 0;
  }

  // What the chat button must stay above, as rectangles on screen. The
  // floating bar's resting place is used rather than its live position, so a
  // bar part-way through sliding in or out never leaves the button hovering
  // at an in-between height.
  function obstacles() {
    var list = [];
    var toolbar = document.querySelector('.mobile-toolbar__icons');
    if (visible(toolbar)) list.push(toolbar.getBoundingClientRect());

    var bar = document.getElementById('wattec-bar');
    if (bar && !bar.classList.contains('wattec-hidden') && visible(bar)) {
      var r = bar.getBoundingClientRect();
      // Its resting height above the bottom of the screen (its "bottom"
      // setting plus its own height) — not where a slide animation has it.
      var rest = (parseFloat(window.getComputedStyle(bar).bottom) || 0) + bar.offsetHeight;
      list.push({ left: r.left, right: r.right, top: window.innerHeight - rest, bottom: window.innerHeight });
    }

    var toTop = document.getElementById('to-top');
    if (toTop && toTop.classList.contains('show') && visible(toTop)) list.push(toTop.getBoundingClientRect());
    return list;
  }

  /* The chat button steps out of the way while the cart drawer is open (it
     sat over the drawer's Checkout button) and comes back when it closes —
     on every screen size. The drawer only; the cart page is unaffected.
     Called from update(), which already runs on every class change on the
     page — the drawer opening and closing is one. */
  function syncCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    var open = !!(drawer && drawer.classList.contains('active'));
    root.classList.toggle('wt-cart-drawer-open', open);
    var host = document.querySelector('shopify-chat');
    if (host) host.classList.toggle('wt-drawer-hide', open);

    // The Go to Cart button stays while the drawer is open where the drawer
    // is clear of it (desktop: it doubles as a close toggle), but steps aside
    // where the drawer covers it (phone: it sat on the Check Out button).
    // The drawer's resting place is worked out from its width — it is
    // anchored to the right edge — so a drawer still sliding in counts.
    var covers = false;
    var gtc = document.getElementById('wattec-gotocart');
    var panel = open && drawer.querySelector('.drawer__inner');
    if (panel && gtc) {
      var left = window.innerWidth - panel.getBoundingClientRect().width;
      covers = gtc.getBoundingClientRect().right > left - 8;
    }
    root.classList.toggle('wt-drawer-covers-gtc', covers);
  }

  function update() {
    syncCartDrawer();
    var button = chatButton();
    if (!button || !visible(button)) return;
    var r = button.getBoundingClientRect();
    // Where the button would sit with no lift at all: its position on screen
    // minus whatever lift is applied right now (read from the button itself,
    // so a lift still animating into place is accounted for exactly).
    var applied = 0;
    var transform = window.getComputedStyle(button).transform;
    if (transform && transform !== 'none') {
      var parts = transform.match(/matrix(?:3d)?\(([^)]+)\)/);
      if (parts) {
        var values = parts[1].split(',').map(parseFloat);
        applied = values.length === 16 ? values[13] : values[5];
      }
    }
    var naturalBottom = r.bottom - applied;
    var height = r.height;
    var list = obstacles();

    // The Inbox app's own height for the button was chosen to clear the old
    // phone bottom menu bar, which is gone, so the button rests down in the
    // corner (RESTING_GAP above the bottom edge) and only steps up when
    // something is actually underneath it — the back-to-top button, or the
    // floating bar. The same on phones, tablets and desktop.
    var start = Math.max(naturalBottom, window.innerHeight - RESTING_GAP);
    var dropped = start - naturalBottom;

    // Lift it clear of whatever it touches, then look again from the new
    // height — clearing the floating bar can put it on the back-to-top
    // button, which then has to be cleared too.
    var need = 0;
    for (var pass = 0; pass < 6; pass++) {
      var bottom = start - need;
      var top = bottom - height;
      var moved = false;
      list.forEach(function (o) {
        var sideways = r.left < o.right && r.right > o.left;
        var vertically = top < o.bottom + GAP && bottom > o.top - GAP;
        if (sideways && vertically) {
          need = Math.max(need, start - (o.top - GAP));
          moved = true;
        }
      });
      if (!moved) break;
    }
    // Positive moves the button up from where Inbox puts it, negative
    // brings it down towards the corner.
    var value = Math.round(Math.max(0, need) - dropped);
    if (value !== lift) {
      lift = value;
      root.style.setProperty('--wt-chat-lift', lift + 'px');
    }
  }

  var queued = false;
  function queue() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () { queued = false; update(); });
  }

  /* While the chat window is open (Inbox adds an "open" attribute to its
     own <shopify-chat> element), the floating Add to Cart bar and the Go to
     Cart button step out of the way — but only if the window actually
     overlaps them. On a phone or tablet it always does (the window fills
     the screen down to its message box); on a laptop or desktop it opens
     clear above them, so they stay. Measured rather than decided by screen
     width, so an in-between screen gets the right answer too.
       - the bar gets the class wt-chat-covered (wattec-floating-atc.liquid)
       - the page gets wt-chat-covers-gtc for Go to Cart (wattec-gotocart.css)
     The chat element can arrive late, and could be replaced, so the page is
     watched for it rather than looked up once. */
  var watchedHost = null;
  var hostObserver = null;
  var coverPoll = null;

  // Where a bottom-fixed element sits when showing — its "bottom" setting
  // and height — not where a slide-away animation currently has it.
  function restingRect(el) {
    var cs = window.getComputedStyle(el);
    var r = el.getBoundingClientRect();
    var bottom = window.innerHeight - (parseFloat(cs.bottom) || 0);
    return { left: r.left, right: r.right, top: bottom - el.offsetHeight, bottom: bottom };
  }

  // The chat window sits inside the chat's own component, nested more than
  // one layer deep, so it is searched for through each layer in turn.
  function findDeep(el, selector) {
    var root = el && el.shadowRoot;
    if (!root) return null;
    var hit = root.querySelector(selector);
    if (hit) return hit;
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) {
        hit = findDeep(all[i], selector);
        if (hit) return hit;
      }
    }
    return null;
  }

  function overlaps(a, b, margin) {
    return a.left < b.right + margin && a.right > b.left - margin &&
      a.top < b.bottom + margin && a.bottom > b.top - margin;
  }

  function syncChatCover() {
    var host = document.querySelector('shopify-chat');
    var open = !!(host && host.hasAttribute('open'));
    root.classList.toggle('wt-chat-open', open);

    var panelRect = null;
    if (open) {
      var panel = findDeep(host, '.panel');
      var pr = panel && panel.getBoundingClientRect();
      if (pr && pr.width > 0 && pr.height > 0) panelRect = pr;
    }
    // If the window can't be measured (Inbox changed how it's built), err
    // on the side of stepping aside, as before.
    var covers = function (el) {
      return open && !!el && (!panelRect || overlaps(restingRect(el), panelRect, 8));
    };

    var bar = document.getElementById('wattec-bar');
    if (bar) bar.classList.toggle('wt-chat-covered', covers(bar));
    root.classList.toggle('wt-chat-covers-gtc', covers(document.getElementById('wattec-gotocart')));

    // The window animates open and can change size as a conversation grows,
    // so it's re-measured for as long as it stays open.
    if (open && !coverPoll) coverPoll = setInterval(syncChatCover, 400);
    if (!open && coverPoll) { clearInterval(coverPoll); coverPoll = null; }
  }

  function syncChatOpen() {
    var host = document.querySelector('shopify-chat');
    if (host !== watchedHost) {
      if (hostObserver) hostObserver.disconnect();
      watchedHost = host;
      if (host) {
        hostObserver = new MutationObserver(syncChatOpen);
        hostObserver.observe(host, { attributes: true, attributeFilter: ['open'] });
      }
    }
    syncChatCover();
    setTimeout(syncChatCover, 150);
  }

  function start() {
    syncChatOpen();
    window.addEventListener('resize', syncChatCover);
    setInterval(function () {
      if (document.querySelector('shopify-chat') !== watchedHost) syncChatOpen();
    }, 800);
    window.addEventListener('resize', queue);
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('orientationchange', function () { setTimeout(queue, 300); });
    // The floating bar showing or hiding, and the back-to-top button
    // appearing, are both class changes.
    new MutationObserver(queue).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });
    // The chat loads late and on its own schedule, so keep checking gently.
    setInterval(update, 800);
    update();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
